import React, { createContext, useContext, useState, useEffect } from 'react';
import { getToken, storeToken, clearAuth, getStoredUser, storeUser } from '../utils/auth';
import apiService from '../services/apiService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize authentication state
  useEffect(() => {
    const initAuth = () => {
      try {
        const storedToken = getToken();
        const storedUser = getStoredUser();

        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(storedUser);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = (userData, authToken, rememberMe = true) => {
    try {
      // Store token and user data
      storeToken(authToken, rememberMe);
      storeUser(userData, rememberMe);

      // Update state
      setToken(authToken);
      setUser(userData);
      setIsAuthenticated(true);

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  // New API-based login method
  const loginWithCredentials = async (credentials, rememberMe = true) => {
    try {
      setLoading(true);
      
      // Use apiService which handles CSRF tokens automatically
      const response = await apiService.login(credentials);
      
      if (response.success && response.data?.token) {
        // Store token and user data
        storeToken(response.data.token, rememberMe);
        storeUser(response.data.user, rememberMe);

        // Update state
        setToken(response.data.token);
        setUser(response.data.user);
        setIsAuthenticated(true);

        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      console.error('API Login error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Call API logout to invalidate session on server
      try {
        await apiService.logout();
      } catch (apiError) {
        console.warn('API logout failed, proceeding with local logout:', apiError);
      }

      // Clear stored auth data
      clearAuth();

      // Reset state
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);

      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  };

  const updateUser = (updatedUserData) => {
    try {
      // Preserve the remember preference from token storage location
      const rememberMe = !!localStorage.getItem('token');
      storeUser(updatedUserData, rememberMe);
      setUser(updatedUserData);
      return { success: true };
    } catch (error) {
      console.error('Update user error:', error);
      return { success: false, error: error.message };
    }
  };

  // Check if token is expired or invalid
  const validateToken = async () => {
    if (!token) return false;

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/auth/validate`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        return true;
      } else {
        // Token is invalid, logout user
        logout();
        return false;
      }
    } catch (error) {
      console.error('Token validation error:', error);
      // If validation fails, assume token is invalid
      logout();
      return false;
    }
  };

  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    login,
    loginWithCredentials,
    logout,
    updateUser,
    validateToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;